import { ReactNode, SyntheticEvent, useState } from 'react';

import { useProductFeedback } from '@lib/core/products/hooks/useProductFeedback';
import { TParsedProductInstance } from '@lib/core/products/types';
import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { useRetailerLocation } from '@lib/core/retailers/hooks/useRetailerLocation';
import { BrowserUrlConsts, REQUEST_METHODS, isApplicationKiosk } from '@lib/core/service/consts';
import { useWidgetInstance } from '@lib/core/service/hooks/useWidgetInstance';
import { request } from '@lib/core/service/requests/request';
import { createMobileKioskQRUrl } from '@lib/core/service/utils';
import { addToEcommerceCart } from '@lib/tools/comms/utils';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import {
  MP_MAIN_INTERACTION_CLICK,
  MixpanelMainInteractionClick,
  MixpanelPositionContext,
} from '@lib/tools/dat/mixpanel/consts';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import { useModals } from '@lib/tools/modals/hooks';
import { MODALS } from '@lib/tools/shared/helpers/consts';

import { FindMeModal as KioskFindMeModal } from '@components/web/src/templates/Modals/FindMeModal/Kiosk/FindMeModal';
import { FindMeModal as WidgetFindMeModal } from '@components/web/src/templates/Modals/FindMeModal/Web/FindMeModal';
import { ProductRateModal } from '@components/web/src/templates/Modals/ProductRateModal/ProductRateModal';

interface Props {
  parsedProductInstanceData: TParsedProductInstance;
  mixpanelIndex: number | null;
  mixpanelPositionContext: MixpanelPositionContext;
  isProductPage?: boolean;
  renderButton: (props: {
    text: string;
    isDisabledButton?: boolean;
    onClick: (e?: SyntheticEvent) => void;
  }) => ReactNode;
}

export const CustomInteraction = ({
  parsedProductInstanceData,
  mixpanelIndex,
  mixpanelPositionContext,
  isProductPage = false,
  renderButton,
}: Props) => {
  const {
    productCard: { tasteItBtn, findMeBtn, rateItBtn, selectBtn, addToCart, unavailable },
    productDetails: { findItOnShelf },
  } = localeCommon;

  const [isFindMeModalOpen, setIsFindMeModalOpen] = useState(false);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);

  const { handleToggleOpenedFeedbackModal } = useProductFeedback();
  const { retailerSlug } = useRetailer();
  const { retailerLocationSlug, associatedRetailerLocation } = useRetailerLocation();
  const { handleToggleModal } = useModals();

  const {
    productInstanceId,
    productInstanceData,
    productFeedbackStatus,
    isMobileKiosk,
    isFindProductFunctionalityEnable,
    isEnableVusionAddon,
    productEcommerceId,
    productRateOptions,
    productProducerUrl,
    productRate,
    isFindMeClicked,
    isGameEnabled,
    handleUpdateProductRating,
    isProductRateUpdateLoading,
    handleRecordFindMeClickTimeById,
    isAddToCartAddon,
  } = parsedProductInstanceData || {};
  const { productInteraction, handleResetProductInteraction } = useWidgetInstance();

  const trackProductMainInteractionClick = (mainInteractionText: MixpanelMainInteractionClick) => {
    if (isProductPage) return;

    MixpanelTracker.events.productMainInteractionClick(parsedProductInstanceData, mainInteractionText);
  };

  const handleOpenProductFeedbackModal = (e?: SyntheticEvent) => {
    e?.stopPropagation();
    handleToggleOpenedFeedbackModal({ productInstanceData });

    trackProductMainInteractionClick(MP_MAIN_INTERACTION_CLICK.TASTE_IT);
  };

  const handleOpenFindMeModal = (e?: SyntheticEvent) => {
    e?.stopPropagation();
    setIsFindMeModalOpen(true);

    if (!isApplicationKiosk) {
      trackProductMainInteractionClick(MP_MAIN_INTERACTION_CLICK.FIND_ME);
    }
    MixpanelTracker.events.findMe(parsedProductInstanceData, mixpanelIndex, mixpanelPositionContext);

    if (isEnableVusionAddon) {
      const locationSlug = isApplicationKiosk
        ? retailerLocationSlug
        : associatedRetailerLocation?.slug || retailerLocationSlug;

      request({
        method: REQUEST_METHODS.POST,
        url: `/${retailerSlug}/${locationSlug}/product/instance/${productInstanceId}/flash/`,
      });
    }
  };
  const mobileKioskQRUrl = createMobileKioskQRUrl({
    [BrowserUrlConsts.shouldRedirectToProductPage]: 'true',
    [BrowserUrlConsts.productId]: productInstanceId,
  });

  const handleOpenRateModal = (e?: SyntheticEvent) => {
    e?.stopPropagation();
    setIsRateModalOpen(true);

    trackProductMainInteractionClick(MP_MAIN_INTERACTION_CLICK.RATE_IT);
  };

  const handleAddToCartClick = (e?: SyntheticEvent) => {
    e?.stopPropagation();
    addToEcommerceCart(productEcommerceId, 1);

    trackProductMainInteractionClick(MP_MAIN_INTERACTION_CLICK.ADD_TO_CART);
    if (isProductPage) {
      MixpanelTracker.events.contactTheProductProducer(parsedProductInstanceData);
    }

    if (productInteraction?.productId && productInteraction.productId === productInstanceId) {
      handleResetProductInteraction();
    }

    window.open(productProducerUrl, '_top');
  };

  let renderButtonText;
  let renderButtonHandleClick;
  let isShowRenderButton = true;
  let isDisabledButton = false;

  let findMeButtonHandleClick;

  if (isAddToCartAddon) {
    if (productProducerUrl) {
      renderButtonText = addToCart;
      renderButtonHandleClick = handleAddToCartClick;
    } else {
      renderButtonText = unavailable;
      isDisabledButton = true;
    }
  } else if (isGameEnabled && !productRate) {
    renderButtonText = selectBtn;
    renderButtonHandleClick = null;
  } else if (isApplicationKiosk) {
    if (isFindProductFunctionalityEnable) {
      renderButtonText = isProductPage ? findItOnShelf : findMeBtn;
      renderButtonHandleClick = handleOpenFindMeModal;
    } else {
      isShowRenderButton = false;
    }
  } else if (isMobileKiosk) {
    if (isFindProductFunctionalityEnable) {
      renderButtonText = findMeBtn;
      renderButtonHandleClick = handleOpenFindMeModal;
    } else {
      isShowRenderButton = false;
    }
  } else if (isFindProductFunctionalityEnable) {
    // Event variant (isFindProductFunctionalityEnable + isInteractiveTastingAddon)
    renderButtonText = findMeBtn;
    renderButtonHandleClick = handleOpenFindMeModal;

    if (isFindMeClicked) {
      if (productFeedbackStatus?.isEnabled) {
        renderButtonText = tasteItBtn;
        renderButtonHandleClick = handleOpenProductFeedbackModal;
      } else {
        renderButtonText = rateItBtn;
        renderButtonHandleClick = handleOpenRateModal;

        if (productRate) {
          isShowRenderButton = false;
        }
      }
    }

    // Find Me Modal
    if (!productRate || productFeedbackStatus?.isEnabled) {
      findMeButtonHandleClick = () => {
        handleRecordFindMeClickTimeById?.();
        (productFeedbackStatus?.isEnabled ? handleOpenProductFeedbackModal : handleOpenRateModal)();
        setIsFindMeModalOpen(false);
      };
    }
  } else if (productFeedbackStatus?.isEnabled) {
    renderButtonText = tasteItBtn;
    renderButtonHandleClick = handleOpenProductFeedbackModal;
  } else {
    renderButtonText = rateItBtn;
    renderButtonHandleClick = handleOpenRateModal;

    if (productRate) {
      isShowRenderButton = false;
    }
  }
  const isShowKioskFindMeModal = isFindMeModalOpen && isApplicationKiosk && parsedProductInstanceData;
  const isShowWidgetFindMeModal = isFindMeModalOpen && !isApplicationKiosk && parsedProductInstanceData;
  return (
    <>
      {isShowRenderButton &&
        renderButton({ isDisabledButton, onClick: renderButtonHandleClick, text: renderButtonText })}

      {isShowWidgetFindMeModal && (
        <WidgetFindMeModal
          handleBtnClick={findMeButtonHandleClick}
          hideModal={() => setIsFindMeModalOpen(false)}
          isOpen={isFindMeModalOpen}
          parsedProductInstanceData={parsedProductInstanceData}
        />
      )}

      {isShowKioskFindMeModal && (
        <KioskFindMeModal
          hideModal={() => setIsFindMeModalOpen(false)}
          isOpen={isFindMeModalOpen}
          parsedProductInstanceData={parsedProductInstanceData}
          qrCodeUrl={mobileKioskQRUrl}
        />
      )}

      {isRateModalOpen && parsedProductInstanceData && (
        <ProductRateModal
          handleOpenDiscoveryQuizModal={() => handleToggleModal(MODALS.DISCOVERY_QUIZ_MODAL)}
          handleUpdateProductRating={handleUpdateProductRating}
          hideModal={() => setIsRateModalOpen(false)}
          isLoading={isProductRateUpdateLoading}
          isModalOpen={isRateModalOpen}
          parsedProductInstanceData={parsedProductInstanceData}
          productRateOptions={productRateOptions}
        />
      )}
    </>
  );
};
